import { Dropdown, Form } from 'react-bootstrap';
import React, { useState, useEffect } from 'react';

export const createColumnToggleDropdown = (columnVisibility, columnLabels, toggleColumnVisibility) => {
    return (
        <Dropdown>
            <Dropdown.Toggle variant="light" id="dropdown-basic">
                Toggle Columns
            </Dropdown.Toggle>

            <Dropdown.Menu>
                {/* Skip columns without a label (e.g. in_transit when warehouse transit is disabled) */}
                {Object.keys(columnVisibility).filter((column) => columnLabels[column]).map((column) => (
                    <Dropdown.Item key={column} onClick={() => toggleColumnVisibility(column)}>
                        <Form.Check 
                            type="checkbox"
                            label={columnLabels[column]}
                            checked={columnVisibility[column]}
                            onClickCapture={(e) => {
                                e.stopPropagation();
                                toggleColumnVisibility(column);
                            }}
                        />
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
};

export const createFilterDropdown = ({
    selectedValue,
    handleSelect,
    items,
    defaultLabel,
    allItemsLabel = "All",
    style = {
        fontWeight: 'bold',
        padding: '0rem 0.5rem 0.35rem',
        height: '25px'
    }
}) => {
    return (
        <Dropdown onSelect={handleSelect}>
            <Dropdown.Toggle 
                variant="light" 
                id={`${(defaultLabel || 'filter').toLowerCase()}-filter-dropdown`}
                style={style}
            >
                {selectedValue ? items[selectedValue] : (defaultLabel || 'Filter')}
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <Dropdown.Item eventKey="">{`${allItemsLabel} ${(defaultLabel || 'Item')}s`}</Dropdown.Item>
                {Object.entries(items).map(([id, name]) => (
                    <Dropdown.Item key={id} eventKey={id}>
                        {name}
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
};

export const handleSearchWithDebounce = (searchTerm, setSearchTerm, setCurrentPage, debounceTimeoutRef, delay = 500) => {
    clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
        setSearchTerm(searchTerm.trim());
        setCurrentPage(1);
    }, delay);
};

export const getCountryFlag = (countryCode) => {
    if (!countryCode || countryCode.length !== 2) return '';
    
    const OFFSET = 127397;
    const first = countryCode.charCodeAt(0) + OFFSET;
    const second = countryCode.charCodeAt(1) + OFFSET;
    
    return String.fromCodePoint(first, second);
};

/**
 * Manages table column state (visibility and widths) in localStorage
 */
const NON_REPORTABLE_COLUMNS = ['selection', 'image', 'actions', 'moreActions'];

export const getReportColumns = (tableId, columnVisibility) => {
    let orderedIds;
    const savedOrder = localStorage.getItem(`table_column_order_${tableId}`);
    if (savedOrder) {
        orderedIds = JSON.parse(savedOrder);
    } else {
        orderedIds = Object.keys(columnVisibility);
    }

    const reportColumns = orderedIds.filter(
        id => columnVisibility[id] === true
            && !NON_REPORTABLE_COLUMNS.includes(id)
            && id in columnVisibility
    );

    if (reportColumns.length === 0) return null;
    return reportColumns.join(',');
};

export const useTableState = (tableId, initialState = {}) => {
    const [columnState, setColumnState] = useState(() => {
        const savedState = localStorage.getItem(`table_state_${tableId}`);
        return savedState ? JSON.parse(savedState) : initialState;
    });

    useEffect(() => {
        localStorage.setItem(`table_state_${tableId}`, JSON.stringify(columnState));
    }, [columnState, tableId]);

    const updateColumnVisibility = (columnId, isVisible) => {
        setColumnState(prev => ({
            ...prev,
            visibility: {
                ...prev.visibility,
                [columnId]: isVisible
            }
        }));
    };

    const updateColumnWidth = (columnId, width) => {
        setColumnState(prev => ({
            ...prev,
            widths: {
                ...prev.widths,
                [columnId]: width
            }
        }));
    };

    return {
        columnState,
        updateColumnVisibility,
        updateColumnWidth
    };
}; 